import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Play,
  CheckCircle,
  Lock,
  ChevronRight,
  Clock,
  BookOpen,
  Award
} from 'lucide-react';

const LearningPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, token } = useAuth();
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);

  useEffect(() => {
    fetchTraining();
  }, [id]);

  const fetchTraining = async () => {
    try {
      // Try learning endpoint first, fall back to regular endpoint
      let response;
      try {
        response = await api.get(`/learn/trainings/${id}`);
      } catch (e) {
        // If not published, try regular endpoint
        response = await api.get(`/trainings/${id}`);
      }
      setTraining(response.data);
      
      // Set first lesson as current
      if (response.data.modules?.length > 0 && response.data.modules[0].lessons?.length > 0) {
        setCurrentLesson(response.data.modules[0].lessons[0]);
      }
    } catch (error) {
      toast.error('Formation introuvable');
      navigate('/trainings');
    } finally {
      setLoading(false);
    }
  };

  const selectLesson = (moduleIndex, lessonIndex) => {
    const lesson = training.modules[moduleIndex].lessons[lessonIndex];
    
    // Check if sequential and previous lessons not completed
    if (training.is_sequential && moduleIndex > 0 || lessonIndex > 0) {
      let prevCompleted = true;
      for (let mi = 0; mi <= moduleIndex; mi++) {
        const maxLi = mi === moduleIndex ? lessonIndex : training.modules[mi].lessons.length;
        for (let li = 0; li < maxLi; li++) {
          if (!training.modules[mi].lessons[li].completed) {
            prevCompleted = false;
            break;
          }
        }
        if (!prevCompleted) break;
      }
      
      if (!prevCompleted) {
        toast.error('Complétez d\'abord les leçons précédentes');
        return;
      }
    }
    
    setCurrentModuleIndex(moduleIndex);
    setCurrentLessonIndex(lessonIndex);
    setCurrentLesson(lesson);
  };

  const markAsComplete = async () => {
    if (!currentLesson) return;
    
    try {
      await api.post('/analytics/progress', {
        lesson_id: currentLesson.id,
        progress: 100,
        watch_time: 60
      });
      
      // Update local state
      setTraining(prev => ({
        ...prev,
        modules: prev.modules.map((m, mi) => ({
          ...m,
          lessons: m.lessons.map((l, li) => 
            l.id === currentLesson.id ? { ...l, completed: true, user_progress: 100 } : l
          )
        }))
      }));
      
      toast.success('Leçon terminée !');
      
      // Go to next lesson
      goToNextLesson();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const goToNextLesson = () => {
    const currentModule = training.modules[currentModuleIndex];
    
    if (currentLessonIndex < currentModule.lessons.length - 1) {
      // Next lesson in same module
      selectLesson(currentModuleIndex, currentLessonIndex + 1);
    } else if (currentModuleIndex < training.modules.length - 1) {
      // First lesson of next module
      selectLesson(currentModuleIndex + 1, 0);
    } else {
      toast.success('Formation terminée ! Félicitations 🎉');
    }
  };

  const getVideoUrl = (lesson) => {
    if (lesson.youtube_url) {
      // Extract video ID and create embed URL
      const videoId = lesson.youtube_url.split('v=')[1]?.split('&')[0];
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    // Fall back to our media endpoint
    if (lesson.video_id) {
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      return `${baseUrl}/api/media/${lesson.video_id}/download?auth=${token}`;
    }
    return null;
  };

  const calculateProgress = () => {
    if (!training) return 0;
    let completed = 0;
    let total = 0;
    training.modules.forEach(m => {
      m.lessons.forEach(l => {
        total++;
        if (l.completed) completed++;
      });
    });
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!training) return null;

  const progress = calculateProgress();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="font-semibold text-slate-900 font-['Outfit']">
                {training.title}
              </h1>
              <p className="text-sm text-slate-500">
                {progress}% complété
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="w-48 hidden sm:block">
              <Progress value={progress} className="h-2" />
            </div>
            {progress === 100 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">
                <Award className="w-4 h-4" />
                Terminé
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Video Player Area */}
        <div className="flex-1 p-4 lg:p-6">
          <div className="max-w-4xl mx-auto">
            {/* Video */}
            <div className="aspect-video bg-slate-900 rounded-xl overflow-hidden mb-6">
              {currentLesson ? (
                getVideoUrl(currentLesson) ? (
                  currentLesson.youtube_url ? (
                    <iframe
                      src={getVideoUrl(currentLesson)}
                      className="w-full h-full"
                      allowFullScreen
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    />
                  ) : (
                    <video
                      src={getVideoUrl(currentLesson)}
                      controls
                      className="w-full h-full"
                    />
                  )
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white">
                    <div className="text-center">
                      <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p className="text-slate-400">Aucune vidéo assignée à cette leçon</p>
                    </div>
                  </div>
                )
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white">
                  <p className="text-slate-400">Sélectionnez une leçon</p>
                </div>
              )}
            </div>

            {/* Lesson Info */}
            {currentLesson && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="text-sm text-blue-600 font-medium">
                      Module {currentModuleIndex + 1} • Leçon {currentLessonIndex + 1}
                    </span>
                    <h2 className="text-xl font-semibold text-slate-900 mt-1">
                      {currentLesson.title}
                    </h2>
                  </div>
                  {currentLesson.completed ? (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Terminé
                    </div>
                  ) : (
                    <Button
                      data-testid="mark-complete-btn"
                      onClick={markAsComplete}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Marquer comme terminé
                    </Button>
                  )}
                </div>
                
                {currentLesson.description && (
                  <p className="text-slate-600">{currentLesson.description}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Course Content */}
        <div className="w-80 border-l border-slate-200 bg-white overflow-y-auto hidden lg:block">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Contenu de la formation</h3>
            <p className="text-sm text-slate-500 mt-1">
              {training.modules.reduce((sum, m) => sum + m.lessons.length, 0)} leçons
            </p>
          </div>
          
          <div className="divide-y divide-slate-100">
            {training.modules.map((module, moduleIndex) => (
              <div key={module.id}>
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">
                      {moduleIndex + 1}. {module.title}
                    </span>
                    <span className="text-xs text-slate-500">
                      {module.lessons.filter(l => l.completed).length}/{module.lessons.length}
                    </span>
                  </div>
                </div>
                
                <div>
                  {module.lessons.map((lesson, lessonIndex) => {
                    const isActive = currentLesson?.id === lesson.id;
                    const isLocked = training.is_sequential && !lesson.completed && 
                      (lessonIndex > 0 && !module.lessons[lessonIndex - 1]?.completed);
                    
                    return (
                      <button
                        key={lesson.id}
                        onClick={() => selectLesson(moduleIndex, lessonIndex)}
                        disabled={isLocked}
                        className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-colors ${
                          isActive 
                            ? 'bg-blue-50 border-l-2 border-blue-600' 
                            : 'hover:bg-slate-50'
                        } ${isLocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid={`lesson-nav-${lesson.id}`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          lesson.completed 
                            ? 'bg-emerald-500 text-white'
                            : isLocked
                            ? 'bg-slate-200 text-slate-400'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          {lesson.completed ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : isLocked ? (
                            <Lock className="w-3 h-3" />
                          ) : (
                            <Play className="w-3 h-3" />
                          )}
                        </div>
                        <span className={`flex-1 text-sm truncate ${
                          isActive ? 'text-blue-700 font-medium' : 'text-slate-700'
                        }`}>
                          {lesson.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningPage;
